import { Badge, Button, Modal, Text } from '@mantine/core';
import { BADGE_VARIANTS } from 'constants/mantineVariants';
import { FLAG_BADGE, MAPPING_BADGE_LABEL, MAPPING_DESCRIPTION, POST_TAG } from 'constants/mapping';

import { BlockIconSm, CheckIconSm } from '@chownow/pantry/icons';
import GuideCard from './GuideCard';

interface MappingGuideModalProps {
  opened: boolean;
  close: () => void;
}

function FlagBadge({ label }: { label: string }) {
  return (
    <Badge variant={BADGE_VARIANTS.outline} size="xs">
      {label}
    </Badge>
  );
}

function PostTagText({ label }: { label: string }) {
  return <Text variant="body-sm-bold">{label}</Text>;
}

export default function MappingGuideModal({ opened, close }: MappingGuideModalProps) {
  const statusDefinitions = [
    {
      element: (
        <Badge variant={BADGE_VARIANTS.success} size="xs">
          {MAPPING_BADGE_LABEL.MAPPED}
        </Badge>
      ),
      description: MAPPING_DESCRIPTION.MAPPED,
    },
    {
      element: (
        <Badge variant={BADGE_VARIANTS.yellow} size="xs" leftSection={<CheckIconSm />}>
          {MAPPING_BADGE_LABEL.EDITED}
        </Badge>
      ),
      description: MAPPING_DESCRIPTION.EDITED_MAPPED,
    },
    {
      element: (
        <Badge variant={BADGE_VARIANTS.yellow} size="xs" leftSection={<BlockIconSm />}>
          {MAPPING_BADGE_LABEL.EDITED}
        </Badge>
      ),
      description: MAPPING_DESCRIPTION.EDITED_UNMAPPED,
    },
    {
      element: (
        <Badge variant={BADGE_VARIANTS.gray} size="xs">
          {MAPPING_BADGE_LABEL.UNMAPPED}
        </Badge>
      ),
      description: MAPPING_DESCRIPTION.UNMAPPED,
    },
  ];

  const flagDefinitions = [
    {
      element: <FlagBadge label={FLAG_BADGE.AS_INSTRUCTION} />,
      description:
        'This item or modifier is added as an instruction. No ID is mapped or sent to your POS. This action is often used when an item is in your CN menu, but does not exist in your POS.',
    },
    {
      element: <FlagBadge label={FLAG_BADGE.IGNORE} />,
      description:
        'This item or modifier will not be sent to your POS and will be removed from orders.',
    },
    {
      element: <FlagBadge label={FLAG_BADGE.AS_ADDITION} />,
      description:
        'This modifier is mapped as an item in your POS and added to orders as an additional item. This action is often used when something is a modifier in your CN menu, but an item in your POS.',
    },
    {
      element: <FlagBadge label={FLAG_BADGE.REPLACES_MAIN} />,
      description:
        'This modifier will replace the order item with the selected item in your POS. This action is often used when modifiers in your CN menu is not supported by your POS.',
    },
  ];

  const postTagDefinitions = [
    {
      element: <PostTagText label={POST_TAG.MULTIPLE} />,
      description: 'This POS item has been mapped to multiple items in the CN menu.',
    },
    {
      element: <PostTagText label={POST_TAG.MAPPED} />,
      description: 'This POS Item is mapped to one item in the CN menu.',
    },
    {
      element: <PostTagText label={POST_TAG.UNMAPPED} />,
      description: 'This POS Item is not mapped to any items in the CN menu.',
    },
  ];

  return (
    <Modal
      opened={opened}
      onClose={close}
      title="Menu Mapping Guide"
      data-testid="menuInfoModal"
      size="lg"
    >
      <Text variant="body-sm">
        Menu mapping feature allows you to assign POS IDs to items and modifiers in your ChowNow
        menu. The terms and badges below appears in the mapping table.
      </Text>
      <GuideCard definitions={statusDefinitions} title="Status" />
      <GuideCard definitions={flagDefinitions} title="Flag" />
      <GuideCard definitions={postTagDefinitions} title="Post Tag" isPostTag />
      <Button onClick={close} fullWidth mt="md">
        Done
      </Button>
    </Modal>
  );
}
